import * as React from 'react';

import { translate } from '~l10n/client';

import { PaywallScreens } from '~analytics/types';
import { IconButton } from '~shared/components/buttons';
import { IconSidebar } from '~shared/components/icons/standard';
import {
  StyledTab,
  TabList,
  TabPanel,
  TabPanels,
  TabsContext,
  TabsProvider,
} from '~shared/components/Tabs';
import { useDrawer } from '~teacher/components/DrawerProvider/DrawerProvider';
import {
  PaywallDialogGeneric,
  PaywallDispatch,
  usePaywallReducer,
} from '~teacher/components/PaywallDialog';
import {
  BUTTON_WIDTH,
  CLOSE_FIELD_TIMEOUT,
  QuizFilter,
} from '~teacher/components/quizzes';
import {
  AddFolderDialog,
  useAddFolderDialog,
} from '~teacher/components/quizzes/AddFolderDialog';
import { DeletedPanel } from '~teacher/components/quizzes/DeletedPanel/DeletedPanel';
import { DisplayQuizLimitPaywall } from '~teacher/components/quizzes/DisplayQuizLimitPaywall';
import { FolderNotFound } from '~teacher/components/quizzes/FolderNotFound';
import {
  ImportQuizToPersonalDialog,
  useImportQuizToPersonalDialog,
} from '~teacher/components/quizzes/ImportQuizDialog';
import { LibraryPanelByParentId } from '~teacher/components/quizzes/LibraryPanel/LibraryPanelByParentId';
import { LibraryPanelBySearch } from '~teacher/components/quizzes/LibraryPanel/LibraryPanelBySearch';
import { LibraryPanelFree } from '~teacher/components/quizzes/LibraryPanel/LibraryPanelFree';
import { useSearchField } from '~teacher/components/quizzes/QuizFilter/useSearchField';
import {
  QuizPanelControls,
  useCreateQuiz,
} from '~teacher/components/quizzes/QuizListMenu';
import { useNotifyCurrentFolderDeleted } from '~teacher/components/quizzes/QuizPanel/hooks/useNotifyCurrentFolderDeleted';
import { useQuizzesFolder } from '~teacher/components/quizzes/QuizPanel/hooks/useQuizzesFolder';
import { useBreadcrumbs } from '~teacher/components/quizzes/useBreadcrumbs';
import { QuizzesPaywall } from '~teacher/components/QuizzesPaywall';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import { useQuizLimit, useQuizzes } from '~teacher/hook';
import { proCheck } from '~teacher/util/proCheck';

import popupController from '../../../../../../shared/components/popup/PopupController';
import { PersonalQuizzesBreadcrumbs } from '../PersonalQuizzesBreadcrumbs/PersonalQuizzesBreadcrumbs';
import { useParentId } from './hooks/useParentId';

interface Props {
  quizFolderPath?: string;
  quizLimitReachedTime?: number;
}

export function QuizPanel({ quizFolderPath, quizLimitReachedTime }: Props) {
  const router = useSocrativeRouter();
  const { setDrawerOpen } = useDrawer();
  const quizzes = useQuizzes();
  const quizLimit = useQuizLimit();

  const [{ open: quizLimitPaywall, touch }, quizLimitPaywallDispatch] =
    usePaywallReducer();
  const [{ open: featurePaywallOpen }, featurePaywallDispatch] =
    usePaywallReducer();

  const { parentId, setParentId } = useParentId(quizFolderPath);
  const currentFolder = useQuizzesFolder({ folderId: parentId });
  useNotifyCurrentFolderDeleted(currentFolder, parentId, setParentId);

  const isFolderNotFound =
    !!currentFolder.isSuccess && !currentFolder.data && !!parentId;

  const addQuiz = useCreateQuiz({
    quizLimitPaywallDispatch: quizLimitPaywallDispatch,
    parentId: parentId,
    popupController: popupController,
  });

  const { openAddFolderDialog, closeAddFolderDialog, isAddFolderDialogOpen } =
    useAddFolderDialog(featurePaywallDispatch);

  const {
    handleSearchQueryChange,
    searchQuery,
    setIsFieldVisible,
    isFieldVisible,
    filterSiblingRef,
    fieldMaxWidth,
    setFieldMaxWidth,
  } = useSearchField();

  /** @todo Add shared workspace logic here. */
  const isSharedWorkspace = false;
  /** @todo Use shared or personal workspace name. */
  const workspaceTitle = isSharedWorkspace ? '' : translate('Personal');

  const breadcrumbs = useBreadcrumbs(quizFolderPath, quizzes);

  const {
    isImportQuizDialogOpen,
    onCloseImportQuizDialog,
    openImportQuizDialog,
  } = useImportQuizToPersonalDialog({
    paywallDispatch: quizLimitPaywallDispatch,
    currentQuizCount: quizLimit.data?.currentQuizCount,
    quizLimit: quizLimit.data?.limit,
  });

  const closeSearchField = React.useCallback(() => {
    setFieldMaxWidth(BUTTON_WIDTH);
    setTimeout(() => {
      setIsFieldVisible(false);
    }, CLOSE_FIELD_TIMEOUT);
  }, [setFieldMaxWidth, setIsFieldVisible]);

  return (
    <div className="flex-1 max-w-full min-w-0 lg:pl-16">
      <TabsProvider
        id="quizzes-tabs"
        onChange={() => {
          if (isFolderNotFound) {
            router.routeToQuizzes();
          }
        }}
      >
        <div className="relative flex items-start justify-between pb-8">
          <div
            ref={filterSiblingRef}
            className={`relative flex flex-1 overflow-hidden ${
              breadcrumbs.length ? 'pb-0' : 'pb-14'
            } lg:pb-0`}
          >
            <div className="flex lg:hidden items-start py-1 pr-2">
              <div className="flex-none">
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  aria-label={translate('Reveal library menu')}
                >
                  <IconSidebar />
                </IconButton>
              </div>
            </div>
            {breadcrumbs.length ? (
              <PersonalQuizzesBreadcrumbs
                currentItem={currentFolder.data}
                folders={quizzes.data?.itemById}
              />
            ) : (
              <div className="absolute right-0 bottom-0 left-0 lg:static">
                <TabList label={translate('Personal')} orientation="horizontal">
                  <StyledTab item="library">{translate('Quizzes')}</StyledTab>
                  <StyledTab item="deleted">{translate('Deleted')}</StyledTab>
                </TabList>
              </div>
            )}
          </div>
          <div className="flex py-1">
            <TabsContext.Consumer>
              {({ selectedIndex }) =>
                selectedIndex !== 1 ? (
                  <div className="flex gap-2">
                    <QuizFilter
                      setIsFieldVisible={setIsFieldVisible}
                      isFieldVisible={isFieldVisible}
                      siblingRef={filterSiblingRef}
                      fieldMaxWidth={fieldMaxWidth}
                      setFieldMaxWidth={setFieldMaxWidth}
                      onChange={handleSearchQueryChange}
                      aria-label={translate('Search “{0}”').format(
                        workspaceTitle
                      )}
                      placeholder={translate('Search “{0}”').format(
                        workspaceTitle
                      )}
                    />
                    {isFolderNotFound ? null : (
                      <>
                        <QuizPanelControls
                          openAddFolderDialog={() => openAddFolderDialog()}
                          addQuiz={addQuiz}
                          openImportQuizDialog={openImportQuizDialog}
                        />
                      </>
                    )}
                  </div>
                ) : null
              }
            </TabsContext.Consumer>
          </div>
        </div>
        <QuizPanelPanels
          isFieldVisible={isFieldVisible}
          folderNotFound={isFolderNotFound}
          parentId={parentId}
          setParentId={setParentId}
          closeSearchField={closeSearchField}
          searchQuery={searchQuery}
          featurePaywallDispatch={featurePaywallDispatch}
          quizLimitPaywallDispatch={quizLimitPaywallDispatch}
        />
      </TabsProvider>
      <ImportQuizToPersonalDialog
        paywallDispatch={quizLimitPaywallDispatch}
        open={isImportQuizDialogOpen}
        parentId={parentId}
        onClose={onCloseImportQuizDialog}
      />
      <AddFolderDialog
        parentId={parentId}
        onClose={closeAddFolderDialog}
        open={isAddFolderDialogOpen}
      />
      <QuizzesPaywall
        open={quizLimitPaywall}
        onClose={() => quizLimitPaywallDispatch({ type: 'CLOSE' })}
        paywallTouch={touch}
      />
      <PaywallDialogGeneric
        open={featurePaywallOpen}
        onClose={() => featurePaywallDispatch({ type: 'CLOSE' })}
        paywallScreen={PaywallScreens.QuizListAddFolder}
      />
      {quizLimitReachedTime != null ? (
        <DisplayQuizLimitPaywall
          quizLimitReachedTime={quizLimitReachedTime}
          paywallDispatch={quizLimitPaywallDispatch}
        />
      ) : null}
    </div>
  );
}

interface QuizPanelPanelsProps {
  folderNotFound: boolean;
  isFieldVisible: boolean;
  parentId: number;
  setParentId: (newParentId: number) => void;
  featurePaywallDispatch: PaywallDispatch;
  quizLimitPaywallDispatch: PaywallDispatch;
  searchQuery: string;
  closeSearchField: () => void;
}

function QuizPanelPanels({
  folderNotFound,
  isFieldVisible,
  parentId,
  setParentId,
  featurePaywallDispatch,
  quizLimitPaywallDispatch,
  searchQuery,
  closeSearchField,
}: QuizPanelPanelsProps) {
  if (folderNotFound) return <FolderNotFound />;

  if (isFieldVisible)
    return (
      <LibraryPanelBySearch
        searchQuery={searchQuery}
        setParentId={setParentId}
        closeSearchField={closeSearchField}
      />
    );

  return (
    <TabPanels>
      <TabPanel>
        {proCheck.canMakeFolders() ? (
          <LibraryPanelByParentId
            parentId={parentId}
            setParentId={setParentId}
          />
        ) : (
          <LibraryPanelFree />
        )}
      </TabPanel>
      <TabPanel>
        <DeletedPanel
          paywallDispatch={featurePaywallDispatch}
          quizLimitPaywallDispatch={quizLimitPaywallDispatch}
        />
      </TabPanel>
    </TabPanels>
  );
}
